import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { HelpBubble } from "../components/HelpBubble";
import { stepColor } from "../components/ui/status-colors";
import {
  GitBranch, Clock, CheckCircle, XCircle, FileText, RotateCcw, Ban,
  AlertCircle, Filter, Eye, ArrowRight, Send, RefreshCw, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const stageLabels = {
  review: "Under Review",
  revision: "Revision Required",
  approval: "Pending Approval",
  completed: "Completed",
  cancelled: "Cancelled",
};

const stageColors = {
  review: "bg-amber-100 text-amber-700",
  revision: "bg-orange-100 text-orange-700",
  approval: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-600",
};

const statusColors = {
  pending: "bg-blue-100 text-blue-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-600",
};

export default function WorkflowsPage() {
  const { user } = useAuth();
  const [governedWorkflows, setGovernedWorkflows] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState("all");
  const [actionDialog, setActionDialog] = useState({ open: false, workflowId: null, action: null });
  const [actionComment, setActionComment] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [enrichedRes, tasksRes] = await Promise.all([
        apiClient.get("/governed-workflows/enriched"),
        apiClient.get("/governed-workflows/my-tasks"),
      ]);
      setGovernedWorkflows(enrichedRes.data);
      setMyTasks(tasksRes.data);
    } catch (error) {
      toast.error("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAction = (workflowId, action) => {
    setActionDialog({ open: true, workflowId, action });
    setActionComment("");
  };

  const executeAction = async () => {
    const { workflowId, action } = actionDialog;
    try {
      if (action === "review-approve") {
        await apiClient.post(`/governed-workflows/${workflowId}/review-approve${actionComment ? `?comment=${encodeURIComponent(actionComment)}` : ""}`);
        toast.success("Review approved");
      } else if (action === "send-back") {
        await apiClient.post(`/governed-workflows/${workflowId}/send-back?reason=${encodeURIComponent(actionComment || "Revision needed")}`);
        toast.success("Sent back for revision");
      } else if (action === "approve") {
        await apiClient.post(`/governed-workflows/${workflowId}/approve${actionComment ? `?comment=${encodeURIComponent(actionComment)}` : ""}`);
        toast.success("Approved");
      } else if (action === "reject") {
        await apiClient.post(`/governed-workflows/${workflowId}/reject?comment=${encodeURIComponent(actionComment || "Rejected")}`);
        toast.success("Rejected");
      }
      setActionDialog({ open: false, workflowId: null, action: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const handleCancel = async (workflowId) => {
    if (!window.confirm("Recall this workflow?")) return;
    try {
      await apiClient.post(`/governed-workflows/${workflowId}/cancel`);
      toast.success("Workflow cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    }
  };

  const handleResubmitReview = async (workflowId) => {
    try {
      await apiClient.post(`/governed-workflows/${workflowId}/resubmit-review`);
      toast.success("Resubmitted for review");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resubmit");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const getMyTaskType = (wf) => {
    if (wf.stage === "review") {
      for (const step of wf.review_steps || []) {
        if (step.user_id === user?.id && step.status === "pending") return "review";
      }
    }
    if (wf.stage === "approval") {
      for (const step of wf.approval_steps || []) {
        if (step.user_id === user?.id && step.status === "pending") return "approval";
      }
    }
    return null;
  };

  const isCreator = (wf) => wf.created_by === user?.id;

  const stats = {
    total: governedWorkflows.length,
    review: governedWorkflows.filter((w) => w.stage === "review").length,
    approval: governedWorkflows.filter((w) => w.stage === "approval").length,
    revision: governedWorkflows.filter((w) => w.stage === "revision").length,
    completed: governedWorkflows.filter((w) => w.stage === "completed").length,
  };

  const filtered = stageFilter === "all"
    ? governedWorkflows
    : governedWorkflows.filter((w) => w.stage === stageFilter);

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;

  const StepIndicator = ({ step, index }) => (
    <div className="flex items-center gap-1.5">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${stepColor(step.status)}`}>
        {step.status === "approved" ? <CheckCircle size={12} className="text-emerald-600" /> :
         step.status === "rejected" ? <XCircle size={12} className="text-red-600" /> :
         <span className="text-[10px] font-bold text-slate-400">{index + 1}</span>}
      </div>
      <span className="text-xs text-slate-600">{step.user_name}</span>
      {step.comment && <span className="text-[10px] text-slate-400 italic truncate max-w-[100px]">"{step.comment}"</span>}
    </div>
  );

  const WorkflowCard = ({ wf }) => {
    const taskType = getMyTaskType(wf);
    const sColor = stageColors[wf.stage] || stageColors.review;

    return (
      <Card className="hover:shadow-md transition-shadow" data-testid={`governed-wf-card-${wf.id}`}>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Link to={`/contracts/${wf.contract_id}`} className="font-medium text-slate-900 dark:text-white hover:text-cyan-600 transition-colors truncate" data-testid={`wf-contract-link-${wf.id}`}>
                  {wf.contract_title || "Untitled"}
                </Link>
                <Badge className={`rounded-sm text-xs ${sColor}`}>{stageLabels[wf.stage] || wf.stage}</Badge>
                <Badge className={`rounded-sm text-xs ${statusColors[wf.status] || ""}`}>{wf.status}</Badge>
                {wf.contract_type && <span className="text-xs text-slate-400">{wf.contract_type}</span>}
              </div>
              {wf.supplier_name && <p className="text-sm text-slate-500 mb-1">Supplier: {wf.supplier_name}</p>}
              <p className="text-xs text-slate-400">
                Submitted {formatDate(wf.created_at)}
                {wf.submitted_by_name && ` by ${wf.submitted_by_name}`}
                {wf.contract_value && ` · Value: $${Number(wf.contract_value).toLocaleString()}`}
              </p>

              {/* Stages Progress */}
              <div className="mt-3 flex items-center gap-2">
                {/* Review Stage */}
                <div className="flex flex-col">
                  <p className="text-[10px] uppercase font-semibold text-slate-400 mb-1">Review</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    {wf.review_steps?.map((step, i) => <StepIndicator key={step.user_id || `review-${i}`} step={step} index={i} />)}
                  </div>
                </div>
                <ArrowRight size={16} className="text-slate-300 mx-2 flex-shrink-0 mt-4" />
                {/* Approval Stage */}
                <div className="flex flex-col">
                  <p className="text-[10px] uppercase font-semibold text-slate-400 mb-1">Approval</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    {wf.approval_steps?.map((step, i) => <StepIndicator key={step.user_id || `approval-${i}`} step={step} index={i} />)}
                  </div>
                </div>
              </div>

              {/* Revisions */}
              {wf.revisions?.length > 0 && (
                <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                  <RefreshCw size={12} /> {wf.revisions.length} revision{wf.revisions.length > 1 ? "s" : ""}
                  {wf.revisions[wf.revisions.length - 1]?.reason && ` — "${wf.revisions[wf.revisions.length - 1].reason}"`}
                </p>
              )}

              {wf.due_date && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle size={12} /> Due: {formatDate(wf.due_date)}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
              {/* Review actions */}
              {taskType === "review" && (
                <>
                  {wf._acting_as_delegate && (
                    <Badge className="rounded-sm text-xs bg-purple-100 text-purple-700" data-testid={`delegate-badge-${wf.id}`}>
                      On behalf of {wf._on_behalf_of}
                    </Badge>
                  )}
                  <Button onClick={() => openAction(wf.id, "review-approve")} size="sm" className="gap-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid={`review-approve-btn-${wf.id}`}>
                    <CheckCircle size={14} /> Approve Review
                  </Button>
                  <Button onClick={() => openAction(wf.id, "send-back")} size="sm" variant="outline" className="gap-1.5 rounded-sm text-orange-600 border-orange-200 hover:bg-orange-50" data-testid={`send-back-btn-${wf.id}`}>
                    <RotateCcw size={14} /> Send Back
                  </Button>
                </>
              )}
              {/* Approval actions */}
              {taskType === "approval" && (
                <>
                  {wf._acting_as_delegate && (
                    <Badge className="rounded-sm text-xs bg-purple-100 text-purple-700" data-testid={`delegate-badge-${wf.id}`}>
                      On behalf of {wf._on_behalf_of}
                    </Badge>
                  )}
                  <Button onClick={() => openAction(wf.id, "approve")} size="sm" className="gap-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid={`final-approve-btn-${wf.id}`}>
                    <CheckCircle size={14} /> Approve
                  </Button>
                  <Button onClick={() => openAction(wf.id, "reject")} size="sm" variant="outline" className="gap-1.5 rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid={`final-reject-btn-${wf.id}`}>
                    <XCircle size={14} /> Reject
                  </Button>
                </>
              )}
              {/* Author actions */}
              {wf.stage === "revision" && isCreator(wf) && (
                <Button onClick={() => handleResubmitReview(wf.id)} size="sm" variant="outline" className="gap-1.5 rounded-sm text-blue-600 border-blue-200 hover:bg-blue-50" data-testid={`resubmit-review-btn-${wf.id}`}>
                  <Send size={14} /> Resubmit for Review
                </Button>
              )}
              {wf.status === "pending" && isCreator(wf) && wf.stage !== "revision" && (
                <Button onClick={() => handleCancel(wf.id)} size="sm" variant="outline" className="gap-1.5 rounded-sm" data-testid={`cancel-governed-btn-${wf.id}`}>
                  <Ban size={14} /> Recall
                </Button>
              )}
              <Link to={`/contracts/${wf.contract_id}`}>
                <Button variant="ghost" size="sm" className="rounded-sm"><FileText size={14} /></Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="workflows-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Approval Workflows <HelpBubble tip="Track contracts through the review and approval pipeline. Each workflow enforces your approval policies. Once approved, contracts move to signing — choose Print & Sign (wet-ink) or e-signature if configured." />
        </h1>
        <p className="text-slate-500 mt-1">Governed contract review and approval process</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total", value: stats.total, color: "text-slate-700", bg: "bg-slate-50 dark:bg-slate-800" },
          { label: "In Review", value: stats.review, color: "text-amber-700", bg: "bg-amber-50 dark:bg-amber-900/20" },
          { label: "Revision", value: stats.revision, color: "text-orange-700", bg: "bg-orange-50 dark:bg-orange-900/20" },
          { label: "Approval", value: stats.approval, color: "text-blue-700", bg: "bg-blue-50 dark:bg-blue-900/20" },
          { label: "Completed", value: stats.completed, color: "text-emerald-700", bg: "bg-emerald-50 dark:bg-emerald-900/20" },
        ].map((s) => (
          <div key={s.label} className={`rounded-sm p-3.5 ${s.bg}`} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold mt-0.5 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-3">
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="stage-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            <SelectItem value="review">Under Review</SelectItem>
            <SelectItem value="revision">Revision</SelectItem>
            <SelectItem value="approval">Pending Approval</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="my-tasks" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="my-tasks" className="gap-2" data-testid="tab-my-tasks">
            <Clock size={16} /> My Tasks ({myTasks.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2" data-testid="tab-all-governed">
            <GitBranch size={16} /> All Workflows
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-tasks">
          {myTasks.length > 0 ? (
            <div className="space-y-3">
              {myTasks.map((wf) => <WorkflowCard key={wf.id} wf={wf} />)}
            </div>
          ) : (
            <div className="text-center py-20">
              <CheckCircle size={48} className="mx-auto text-emerald-300 mb-4" />
              <p className="text-slate-500">No pending tasks</p>
              <p className="text-sm text-slate-400 mt-1">You're all caught up!</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="all">
          {filtered.length > 0 ? (
            <div className="space-y-3">
              {filtered.map((wf) => <WorkflowCard key={wf.id} wf={wf} />)}
            </div>
          ) : (
            <div className="text-center py-20">
              <GitBranch size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No workflows found</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => { if (!open) setActionDialog({ open: false, workflowId: null, action: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === "review-approve" ? "Approve Review" :
               actionDialog.action === "send-back" ? "Send Back for Revision" :
               actionDialog.action === "approve" ? "Final Approval" :
               actionDialog.action === "reject" ? "Reject Contract" : "Action"}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.action === "send-back"
                ? "Provide a reason for sending the contract back for revision."
                : actionDialog.action === "reject"
                ? "Please provide a reason for rejection."
                : "Add an optional comment."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-2 block">
              Comment {(actionDialog.action === "send-back" || actionDialog.action === "reject") && <span className="text-red-500">*</span>}
            </Label>
            <Textarea
              value={actionComment}
              onChange={(e) => setActionComment(e.target.value)}
              placeholder={
                actionDialog.action === "send-back" ? "Please revise section 3.2 regarding payment terms..." :
                actionDialog.action === "reject" ? "Reason for rejection..." :
                "Looks good."
              }
              rows={3} className="rounded-sm" data-testid="action-comment-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, workflowId: null, action: null })} className="rounded-sm">Cancel</Button>
            <Button
              onClick={executeAction}
              className={`rounded-sm ${
                actionDialog.action === "reject" ? "bg-red-600 hover:bg-red-700" :
                actionDialog.action === "send-back" ? "bg-orange-600 hover:bg-orange-700" :
                "bg-emerald-600 hover:bg-emerald-700"
              }`}
              disabled={(actionDialog.action === "send-back" || actionDialog.action === "reject") && !actionComment.trim()}
              data-testid="confirm-action-btn"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
